import { z } from "zod";

// Azienda della scheda del browser (piano M1b D1). Vive nello `sessionStorage`, che è di una sola scheda:
// due schede sulla stessa origine lavorano in due aziende, e una scheda duplicata ne copia l'azienda.
// Mai cookie né `localStorage`, condivisi fra le schede. Non è una variabile globale: il router lo passa a
// chi serve (`RouterContext.tabStorage`) e ogni chiamata alle API di un'azienda lo riceve come argomento.
//
// Nella stessa voce vive la Vista Commerciale della scheda (piano M2 D16, Task 22): il Commerciale osservato, che
// `apiRequest` manda con ogni richiesta nell'header `X-Gioia-Commercial-View`. Così due schede guardano come due
// Commerciali diversi, una scheda ricaricata guarda ancora come prima e un'azienda nuova parte senza vista. La vista non
// entra in `tabKey`: cambiarla svuota tutta la cache della scheda (`/cambia-vista` nel router), e le chiavi che
// dipendono da ciò che si vede la portano anche loro (`commercialViewOf`).

/** Gli header che il server legge per l'azienda della richiesta (`apps/server/src/context.ts`). */
export const MEMBERSHIP_HEADER = "x-gioia-membership";
export const PLATFORM_ORGANIZATION_HEADER = "x-gioia-platform-organization";
/** L'header della Vista Commerciale (`apps/server/src/commercial-view.ts`): l'appartenenza osservata. */
export const COMMERCIAL_VIEW_HEADER = "x-gioia-commercial-view";
export const TAB_STORAGE_KEY = "gioia365.scheda";

/**
 * Il Commerciale osservato: l'appartenenza, che va al server, e il nome intero con cui la fascia lo dice subito, anche
 * dopo un ricaricamento. Se la vista vale ancora lo decide il server a ogni richiesta.
 */
export type CommercialView = { membershipId: string; displayName: string };

export type TabContext =
  | { kind: "membership"; membershipId: string; commercialView?: CommercialView }
  | { kind: "platform"; organizationId: string; commercialView?: CommercialView };

// Stessa forma che il server accetta negli header: un valore diverso non arriverebbe mai a un'azienda.
const headerValue = z.string().regex(/^[A-Za-z0-9_-]{1,128}$/);

const commercialViewSchema = z.strictObject({ membershipId: headerValue, displayName: z.string().trim().min(1).max(300) });

const tabContextSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("membership"), membershipId: headerValue, commercialView: commercialViewSchema.optional() }),
  z.strictObject({ kind: z.literal("platform"), organizationId: headerValue, commercialView: commercialViewSchema.optional() }),
]);

/** Un valore qualunque (Storage, stato della cronologia) come contesto della scheda, oppure `null`. */
export function parseTabContext(value: unknown): TabContext | null {
  const parsed = tabContextSchema.safeParse(value);
  return parsed.success ? withCommercialView(parsed.data, parsed.data.commercialView ?? null) : null;
}

/** Un valore qualunque (lo stato della cronologia di `/cambia-vista`) come Vista Commerciale, oppure `null`. */
export function parseCommercialView(value: unknown): CommercialView | null {
  const parsed = commercialViewSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * Lo stesso contesto con un'altra Vista Commerciale, o senza (`null`): soltanto i campi del contesto, mai altro che
 * arrivi insieme all'oggetto, e senza vista il campo non c'è.
 */
export function withCommercialView(tab: TabContext, view: CommercialView | null): TabContext {
  const viewPart = view ? { commercialView: { membershipId: view.membershipId, displayName: view.displayName } } : {};
  return tab.kind === "membership"
    ? { kind: "membership", membershipId: tab.membershipId, ...viewPart }
    : { kind: "platform", organizationId: tab.organizationId, ...viewPart };
}

/** L'appartenenza osservata nella Vista Commerciale della scheda, oppure `null`: la parte `view` delle chiavi di cache. */
export function commercialViewOf(tab: TabContext): string | null {
  return tab.commercialView?.membershipId ?? null;
}

/** Il contesto della scheda; un valore illeggibile o di forma sbagliata vale «nessuno» e sparisce. */
export function readTabContext(storage: Storage): TabContext | null {
  const raw = storage.getItem(TAB_STORAGE_KEY);
  if (raw === null) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    value = undefined;
  }
  const tab = parseTabContext(value);
  if (!tab) storage.removeItem(TAB_STORAGE_KEY);
  return tab;
}

export function writeTabContext(storage: Storage, tab: TabContext): void {
  // Si scrivono soltanto i campi del contesto, mai altro che arrivi insieme all'oggetto.
  storage.setItem(TAB_STORAGE_KEY, JSON.stringify(withCommercialView(tab, tab.commercialView ?? null)));
}

export function clearTabContext(storage: Storage): void {
  storage.removeItem(TAB_STORAGE_KEY);
}

/** La seconda parte delle chiavi di cache di un'azienda: `"m:<membershipId>"` oppure `"p:<organizationId>"`. */
export type TabKey = `m:${string}` | `p:${string}`;

/**
 * La parte della chiave di cache che separa i dati di un'azienda da quelli di un'altra (D2). La Vista Commerciale non ne
 * fa parte: il cambio di vista toglie tutte le query della scheda (`/cambia-vista`).
 */
export function tabKey(tab: TabContext): TabKey {
  return tab.kind === "membership" ? `m:${tab.membershipId}` : `p:${tab.organizationId}`;
}

/** Uno `Storage` che vive soltanto in memoria: una scheda dei test, o il ripiego senza `sessionStorage`. */
export function createMemoryStorage(): Storage {
  const values = new Map<string, string>();
  return {
    get length() {
      return values.size;
    },
    clear: () => values.clear(),
    getItem: (key) => values.get(key) ?? null,
    key: (index) => [...values.keys()][index] ?? null,
    removeItem: (key) => {
      values.delete(key);
    },
    setItem: (key, value) => {
      values.set(key, String(value));
    },
  };
}

/**
 * Lo `sessionStorage` della scheda. Se il browser lo nega (archiviazione disattivata), l'azienda vale finché
 * la pagina resta aperta: meglio che un'applicazione che non parte.
 */
export function browserTabStorage(): Storage {
  try {
    const storage = window.sessionStorage;
    storage.getItem(TAB_STORAGE_KEY);
    return storage;
  } catch {
    return createMemoryStorage();
  }
}
